import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const points = [
  ["What we keep", "Your name, email, phone and delivery address, plus your order history and issued coupons."],
  ["Why we keep it", "To fulfil and track your orders, honour your privileges and secure your account."],
  ["Who sees it", "Only the House. We never sell or share your data with third parties for marketing."],
  ["Payments", "Card details are never stored on Vincen servers."],
];

export const PrivacyTab = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "vincen-my-data.json";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Your data has been exported");
    } catch (err) {
      toast.error(formatApiError(err, "Export failed"));
    }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await api.delete("/account");
      navigate("/");
      await logout();
      toast.success("Your account has been erased");
    } catch (err) {
      toast.error(formatApiError(err, "Could not delete account"));
      setBusy(false);
    }
  };

  return (
    <div data-testid="privacy-tab" className="max-w-2xl">
      <div className="flex items-center gap-3 text-gold">
        <ShieldCheck size={16} />
        <p className="text-xs tracking-[0.3em] uppercase">Your data, your rules</p>
      </div>
      <div className="mt-8 flex flex-col divide-y divide-white/5 border-y border-white/5">
        {points.map(([t, d]) => (
          <div key={t} className="py-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <p className="text-xs tracking-[0.25em] uppercase text-zinc-500">{t}</p>
            <p className="sm:col-span-2 text-sm text-zinc-400 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-4">
        <button
          data-testid="privacy-export-button"
          onClick={exportData}
          className="flex items-center justify-center gap-2 px-8 py-4 border border-gold/50 text-gold text-xs tracking-[0.3em] uppercase hover:bg-gold hover:text-black transition-colors duration-500"
        >
          <Download size={13} /> Download My Data
        </button>
        {user.role !== "admin" && (
          <button
            data-testid="privacy-delete-button"
            onClick={() => setConfirm(true)}
            className="flex items-center justify-center gap-2 px-8 py-4 border border-maroon text-maroon-light text-xs tracking-[0.3em] uppercase hover:bg-maroon hover:text-[#F5F5F5] transition-colors duration-500"
          >
            <Trash2 size={13} /> Delete Account
          </button>
        )}
      </div>

      {confirm && (
        <div data-testid="privacy-delete-confirm" className="mt-8 border border-maroon bg-maroon-deep/30 p-6">
          <p className="font-serif text-xl text-[#F5F5F5]">Erase your place in the House?</p>
          <p className="mt-2 text-sm text-zinc-400">
            This permanently removes your profile, address and coupons. Orders are anonymised.
          </p>
          <div className="mt-6 flex gap-3">
            <button
              data-testid="privacy-delete-confirm-button"
              onClick={deleteAccount}
              disabled={busy}
              className="px-6 py-3 bg-maroon text-[#F5F5F5] text-xs tracking-[0.3em] uppercase hover:bg-maroon-light transition-colors disabled:opacity-50"
            >
              {busy ? "..." : "Yes, erase"}
            </button>
            <button
              data-testid="privacy-delete-cancel-button"
              onClick={() => setConfirm(false)}
              className="px-6 py-3 border border-white/15 text-zinc-400 text-xs tracking-[0.3em] uppercase hover:text-zinc-100 transition-colors"
            >
              Keep my account
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
